import React, { useEffect, useState } from 'react';
import {
  Table,
  Spin,
  notification,
  Input,
  Space,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Upload,
  message,
  Tabs,
  Slider,
  Switch,
  Radio,
  Card,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
  PrinterOutlined,
  EyeOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { SUPER_ADMIN, ADMIN, COORDINATOR, INSTRUCTOR } from '../../../Constants/Roles/roles';
import {
  getAllCertificates,
  getStudentCertificates,
  getAllCoursesByRole,
  getAllUsers,
  issueCertificate,
  createCertificateTemplate,
  updateCertificateTemplate,
  getAllCertificateTemplates,
  getAllUsersOfACourse,
} from '../../../Constants/Api/functions/function';
import { UPLOAD_CERTIFICATE_FILE_URL } from '../../../Constants/Api/azure/azure';
import dayjs from 'dayjs';
import './style.scss';

const { TabPane } = Tabs;

const DEFAULT_LAYOUT = {
  studentName: {
    top: 30,
    left: 10,
    fontSize: 26,
    fontWeight: 'bold',
    textAlign: 'left',
    color: '#1a1a1a',
    visible: true,
  },
  courseName: {
    top: 42,
    left: 10,
    fontSize: 18,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#444444',
    visible: true,
  },
  certificateNumber: {
    top: 54,
    left: 10,
    fontSize: 12,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#666666',
    visible: true,
  },
  issueDate: {
    top: 64,
    left: 10,
    fontSize: 12,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#666666',
    visible: true,
  },
};

const Certificates = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('issued');

  // Modals state
  const [issueCertificateModalOpen, setIssueCertificateModalOpen] = useState(false);
  const [createTemplateModalOpen, setCreateTemplateModalOpen] = useState(false);
  const [viewCertificateModalOpen, setViewCertificateModalOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  // Forms state
  const [form] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [issuingMethod, setIssuingMethod] = useState('template');
  const [certificateFileList, setCertificateFileList] = useState([]);

  // Templates design state
  const [templateFileList, setTemplateFileList] = useState([]);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [templateLayout, setTemplateLayout] = useState(DEFAULT_LAYOUT);
  const [activeFieldName, setActiveFieldName] = useState('studentName');
  const [imageAspectRatio, setImageAspectRatio] = useState(1.414);
  const [printAspectRatio, setPrintAspectRatio] = useState(1.414);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Drag-and-drop state for visual template builder
  const [isDragging, setIsDragging] = useState(false);
  const [draggedField, setDraggedField] = useState(null);

  const getFieldTransform = (textAlign) => {
    if (textAlign === 'center') return 'translate(-50%, -50%)';
    if (textAlign === 'right') return 'translate(-100%, -50%)';
    return 'translate(0%, -50%)'; // default 'left'
  };

  const handleMouseDown = (e, field) => {
    e.preventDefault();
    setActiveFieldName(field);
    setIsDragging(true);
    setDraggedField(field);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !draggedField) return;

    const container = document.getElementById('cert-canvas');
    if (!container) return;

    const rect = container.getBoundingClientRect();

    // Calculate mouse position relative to container in percentage
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    let leftPercent = Math.round((mouseX / rect.width) * 100);
    let topPercent = Math.round((mouseY / rect.height) * 100);

    // Clamp values between 0 and 100
    leftPercent = Math.max(0, Math.min(100, leftPercent));
    topPercent = Math.max(0, Math.min(100, topPercent));

    handleLayoutFieldChange(draggedField, 'left', leftPercent);
    handleLayoutFieldChange(draggedField, 'top', topPercent);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedField(null);
  };

  // Data helpers
  const [allCourses, setAllCourses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);

  const userProfile = useSelector((state) => state.userProfile.userProfile);

  const isAdminOrSuperAdmin =
    userProfile?.role === SUPER_ADMIN || userProfile?.role === ADMIN || userProfile?.role === COORDINATOR || userProfile?.role === INSTRUCTOR;

  const handleCourseChange = async (courseId) => {
    if (!courseId) {
      setEnrolledStudents([]);
      return;
    }
    try {
      const students = await getAllUsersOfACourse(courseId);
      setEnrolledStudents(students || []);
    } catch (error) {
      console.error('Error fetching students of course:', error);
      setEnrolledStudents([]);
    }
  };

  const fetchCertificates = async () => {
    setIsLoading(true);
    try {
      if (isAdminOrSuperAdmin) {
        const [certsData, templatesData] = await Promise.all([
          getAllCertificates(userProfile?.role, userProfile?.id),
          getAllCertificateTemplates(userProfile?.role, userProfile?.id),
        ]);
        setCertificates(certsData || []);
        setFilteredCertificates(certsData || []);
        setAllTemplates(templatesData || []);
        setFilteredTemplates(templatesData || []);
      } else {
        const certsData = await getStudentCertificates(userProfile?.id);
        setCertificates(certsData || []);
        setFilteredCertificates(certsData || []);
      }
    } catch (error) {
      console.error('fetchCertificates error:', error);
      api.error({
        message: 'Error',
        description: 'Failed to load certificates data',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.id) {
      fetchCertificates();
    }
  }, [userProfile?.id, userProfile?.role]);

  const handleSearch = (value) => {
    setSearchText(value);

    if (!value.trim()) {
      setFilteredCertificates(certificates);
      setFilteredTemplates(allTemplates);
      return;
    }

    if (activeTab === 'issued') {
      const filtered = certificates.filter(
        (cert) =>
          cert?.studentName?.toLowerCase().includes(value.toLowerCase()) ||
          cert?.courseName?.toLowerCase().includes(value.toLowerCase()) ||
          cert?.CertificateNumber?.toLowerCase().includes(value.toLowerCase()) ||
          cert?.StudentEmail?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCertificates(filtered);
    } else {
      const filtered = allTemplates.filter(
        (tpl) =>
          tpl?.TemplateName?.toLowerCase().includes(value.toLowerCase()) ||
          tpl?.courseName?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredTemplates(filtered);
    }
  };

  const handleOpenIssueCertificateModal = async () => {
    setIsLoading(true);
    try {
      const [coursesData, usersData, templatesData] = await Promise.all([
        getAllCoursesByRole(userProfile?.role, userProfile?.id),
        getAllUsers(),
        getAllCertificateTemplates(userProfile?.role, userProfile?.id),
      ]);
      setAllCourses(coursesData || []);
      setAllUsers(usersData || []);
      setAllTemplates(templatesData || []);
      setEnrolledStudents([]);
      setIssueCertificateModalOpen(true);
    } catch (error) {
      console.error('Error preparing issue certificate modal:', error);
      message.error('Failed to load courses, students, or templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateTemplateModal = async () => {
    setIsLoading(true);
    try {
      const coursesData = await getAllCoursesByRole(userProfile?.role, userProfile?.id);
      setAllCourses(coursesData || []);
      setCreateTemplateModalOpen(true);
    } catch (error) {
      console.error('Error preparing create template modal:', error);
      message.error('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBeforeUploadTemplate = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(`${file.name} is not an image file`);
      return Upload.LIST_IGNORE;
    }
    const localUrl = URL.createObjectURL(file);
    
    // Pre-calculate local image aspect ratio to prevent canvas collapse
    const img = new Image();
    img.src = localUrl;
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setImageAspectRatio(img.naturalWidth / img.naturalHeight);
      }
    };

    setBackgroundImageUrl(localUrl);
    return false; // prevent immediate upload, handled in submit
  };

  const handleLayoutFieldChange = (field, property, value) => {
    setTemplateLayout((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: value,
      },
    }));
  };

  const handleCancelCreateTemplate = () => {
    setCreateTemplateModalOpen(false);
    templateForm.resetFields();
    setTemplateFileList([]);
    if (backgroundImageUrl && backgroundImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(backgroundImageUrl);
    }
    setBackgroundImageUrl('');
    setTemplateLayout(DEFAULT_LAYOUT);
    setActiveFieldName('studentName');
    setImageAspectRatio(1.414);
    setEditingTemplateId(null);
  };

  const handleCreateTemplate = async (values) => {
    setIsLoading(true);
    try {
      let finalBackgroundImageURL = backgroundImageUrl;

      if (templateFileList.length > 0) {
        const formData = new FormData();
        formData.append('file', templateFileList[0].originFileObj);
        formData.append('fileName', `tpl_${values.templateName.replace(/\s/g, '_')}`);

        const uploadResponse = await fetch(UPLOAD_CERTIFICATE_FILE_URL, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadResponse.json();

        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Failed to upload background image');
        }
        finalBackgroundImageURL = uploadData.url;
      } else if (!editingTemplateId) {
        message.error('Please upload a template background design image');
        setIsLoading(false);
        return;
      }

      const templateData = {
        TemplateName: values.templateName,
        BackgroundImageURL: finalBackgroundImageURL,
        LayoutJSON: JSON.stringify(templateLayout),
        CourseID: values.courseId,
      };

      let res;
      if (editingTemplateId) {
        res = await updateCertificateTemplate({
          ...templateData,
          TemplateID: editingTemplateId,
        });
      } else {
        res = await createCertificateTemplate(templateData);
      }

      if (res && res.status === 200) {
        api.success({
          message: 'Success',
          description: editingTemplateId
            ? 'Certificate template updated successfully'
            : 'Certificate template created successfully',
        });
        handleCancelCreateTemplate();
        await fetchCertificates();
      } else {
        api.error({
          message: 'Error',
          description: res?.data?.message || `Failed to ${editingTemplateId ? 'update' : 'create'} template`,
        });
      }
    } catch (error) {
      console.error('handleCreateTemplate error:', error);
      api.error({
        message: 'Error',
        description: error.message || 'Something went wrong',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleIssueCertificate = async (values) => {
    setIsLoading(true);
    try {
      let finalCertificateURL = null;

      if (issuingMethod === 'manual') {
        if (certificateFileList.length < 1) {
          message.error('Please upload a certificate file for manual issuing');
          setIsLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', certificateFileList[0].originFileObj);
        formData.append('fileName', `cert_${values.certificateNumber}`);

        const uploadResponse = await fetch(UPLOAD_CERTIFICATE_FILE_URL, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadResponse.json();

        if (!uploadData.success) {
          throw new Error(uploadData.message || 'Failed to upload certificate file');
        }
        finalCertificateURL = uploadData.url;
      }

      const certificateData = {
        StudentID: values.studentId,
        CourseID: values.courseId,
        CertificateNumber: values.certificateNumber,
        CertificateURL: finalCertificateURL,
        TemplateID: issuingMethod === 'template' ? values.templateId : null,
        IssueDate: values.issueDate
          ? values.issueDate.format('YYYY-MM-DD')
          : new Date().toISOString().split('T')[0],
      };

      const res = await issueCertificate(certificateData);
      if (res && res.status === 200) {
        api.success({
          message: 'Success',
          description: 'Certificate issued successfully',
        });
        form.resetFields();
        setCertificateFileList([]);
        setIssueCertificateModalOpen(false);
        await fetchCertificates();
      } else {
        api.error({
          message: 'Error',
          description: res?.data?.message || 'Failed to issue certificate',
        });
      }
    } catch (error) {
      console.error('handleIssueCertificate error:', error);
      api.error({
        message: 'Error',
        description: error.message || 'Something went wrong',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewCertificate = (record) => {
    if (record.TemplateID) {
      setSelectedCertificate(record);
      setViewCertificateModalOpen(true);
    } else if (record.CertificateURL) {
      window.open(record.CertificateURL, '_blank');
    } else {
      api.error({
        message: 'Error',
        description: 'No certificate template or upload URL available',
      });
    }
  };

  const handlePrintCertificate = () => {
    window.print();
  };

  const issuedColumns = [
    {
      title: 'Certificate Number',
      dataIndex: 'CertificateNumber',
      key: 'CertificateNumber',
      width: 150,
      sorter: (a, b) => (a.CertificateNumber || '').localeCompare(b.CertificateNumber || ''),
    },
    {
      title: 'Student Name',
      dataIndex: 'studentName',
      key: 'studentName',
      width: 150,
      sorter: (a, b) => (a.studentName || '').localeCompare(b.studentName || ''),
    },
    ...(isAdminOrSuperAdmin
      ? [
          {
            title: 'Student Email',
            dataIndex: 'StudentEmail',
            key: 'StudentEmail',
            width: 180,
            sorter: (a, b) => (a.StudentEmail || '').localeCompare(b.StudentEmail || ''),
          },
        ]
      : []),
    {
      title: 'Course Name',
      dataIndex: 'courseName',
      key: 'courseName',
      width: 180,
      sorter: (a, b) => (a.courseName || '').localeCompare(b.courseName || ''),
    },
    {
      title: 'Type',
      dataIndex: 'TemplateID',
      key: 'type',
      width: 120,
      render: (templateId) => (templateId ? 'Template-Based' : 'Manual Upload'),
    },
    {
      title: 'Issue Date',
      dataIndex: 'IssueDate',
      key: 'IssueDate',
      width: 120,
      sorter: (a, b) => new Date(a.IssueDate) - new Date(b.IssueDate),
      render: (text) => text || '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewCertificate(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const templateColumns = [
    {
      title: 'Background Preview',
      dataIndex: 'BackgroundImageURL',
      key: 'BackgroundImageURL',
      width: 150,
      render: (url) => (
        <img
          src={url}
          alt="Bg"
          style={{ width: '100px', height: '70px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #eee' }}
        />
      ),
    },
    {
      title: 'Template Name',
      dataIndex: 'TemplateName',
      key: 'TemplateName',
      sorter: (a, b) => (a.TemplateName || '').localeCompare(b.TemplateName || ''),
    },
    {
      title: 'Course Associated',
      dataIndex: 'courseName',
      key: 'courseName',
      sorter: (a, b) => (a.courseName || '').localeCompare(b.courseName || ''),
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '-'),
      sorter: (a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt),
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={async () => {
              setIsLoading(true);
              try {
                const [usersData, coursesData] = await Promise.all([
                  getAllUsers(),
                  getAllCoursesByRole(userProfile?.role, userProfile?.id),
                ]);
                setAllUsers(usersData);
                setAllCourses(coursesData);
                setAllTemplates(allTemplates);
                setIssuingMethod('template');
                form.setFieldsValue({
                  templateId: record.TemplateID,
                  courseId: record.CourseID,
                });
                await handleCourseChange(record.CourseID);
                setIssueCertificateModalOpen(true);
              } catch (error) {
                console.error('Error opening issue modal:', error);
                message.error('Failed to load students');
              } finally {
                setIsLoading(false);
              }
            }}
          >
            Issue
          </Button>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={async () => {
              setIsLoading(true);
              try {
                const coursesData = await getAllCoursesByRole(userProfile?.role, userProfile?.id);
                setAllCourses(coursesData);
                setEditingTemplateId(record.TemplateID);
                templateForm.setFieldsValue({
                  templateName: record.TemplateName,
                  courseId: record.CourseID,
                });

                // Pre-calculate image aspect ratio to prevent canvas collapse or layout shift
                if (record.BackgroundImageURL) {
                  await new Promise((resolve) => {
                    const img = new Image();
                    img.src = record.BackgroundImageURL;
                    img.onload = () => {
                      if (img.naturalWidth && img.naturalHeight) {
                        setImageAspectRatio(img.naturalWidth / img.naturalHeight);
                      }
                      resolve();
                    };
                    img.onerror = () => {
                      resolve(); // resolve anyway to avoid blocking flow
                    };
                  });
                }

                setBackgroundImageUrl(record.BackgroundImageURL);
                setTemplateLayout(record.LayoutJSON ? JSON.parse(record.LayoutJSON) : DEFAULT_LAYOUT);
                setCreateTemplateModalOpen(true);
              } catch (error) {
                console.error('Error opening edit template modal:', error);
                message.error('Failed to load courses');
              } finally {
                setIsLoading(false);
              }
            }}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const currentLayoutForPreview = selectedCertificate?.LayoutJSON
    ? JSON.parse(selectedCertificate.LayoutJSON)
    : DEFAULT_LAYOUT;

  return (
    <div className="certificates-container">
      {contextHolder}

      <div className="certificates-header">
        <h1>{isAdminOrSuperAdmin ? 'Certificates Management' : 'My Certificates'}</h1>
        {isAdminOrSuperAdmin && (
          <div>
            {activeTab === 'issued' ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenIssueCertificateModal}
              >
                Issue Certificate
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreateTemplateModal}
              >
                Create Template
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="certificates-search">
        <Space>
          <Input
            placeholder={
              activeTab === 'issued'
                ? 'Search by number, name, course or email...'
                : 'Search template name or course...'
            }
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: '400px' }}
            allowClear
          />
        </Space>
      </div>

      <Divider />

      <Spin spinning={isLoading} tip="Loading certificates...">
        {isAdminOrSuperAdmin ? (
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setSearchText('');
            }}
          >
            <TabPane tab="Issued Certificates" key="issued">
              <Table
                columns={issuedColumns}
                dataSource={filteredCertificates}
                rowKey="CertificateID"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1000 }}
                className="certificates-table"
              />
            </TabPane>
            <TabPane tab="Certificate Templates" key="templates">
              <Table
                columns={templateColumns}
                dataSource={filteredTemplates}
                rowKey="TemplateID"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1000 }}
                className="certificates-table"
              />
            </TabPane>
          </Tabs>
        ) : (
          <Table
            columns={issuedColumns}
            dataSource={filteredCertificates}
            rowKey="CertificateID"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
            className="certificates-table"
          />
        )}
      </Spin>

      {/* CREATE CERTIFICATE TEMPLATE (VISUAL BUILDER) */}
      {isAdminOrSuperAdmin && (
        <Modal
          title={editingTemplateId ? 'Edit Certificate Template Design' : 'Create Certificate Template Design'}
          open={createTemplateModalOpen}
          onCancel={handleCancelCreateTemplate}
          footer={null}
          width={1100}
          className="visual-builder-modal"
          style={{ top: 20 }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <h3 style={{ marginBottom: 12 }}>Live Canvas Design Preview</h3>
              <div
                className="certificate-canvas-container"
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: imageAspectRatio,
                  overflow: 'hidden',
                  border: '2px solid #e8e8e8',
                  borderRadius: '12px',
                  background: '#1f1f1f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              >
                {backgroundImageUrl ? (
                  <div
                    id="cert-canvas"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      userSelect: 'none',
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    <img
                      src={backgroundImageUrl}
                      alt="Bg"
                      style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                      onLoad={(e) => {
                        const { naturalWidth, naturalHeight } = e.target;
                        if (naturalWidth && naturalHeight) {
                          setImageAspectRatio(naturalWidth / naturalHeight);
                        }
                      }}
                    />
                    {templateLayout.studentName.visible && (
                      <div
                        onMouseDown={(e) => handleMouseDown(e, 'studentName')}
                        style={{
                          position: 'absolute',
                          top: `${templateLayout.studentName.top}%`,
                          left: `${templateLayout.studentName.left}%`,
                          transform: getFieldTransform(templateLayout.studentName.textAlign),
                          fontSize: `${templateLayout.studentName.fontSize}px`,
                          fontWeight: templateLayout.studentName.fontWeight,
                          color: templateLayout.studentName.color,
                          textAlign: templateLayout.studentName.textAlign,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                          cursor: 'move',
                          width: 'auto',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: activeFieldName === 'studentName' 
                            ? '1.5px solid #1890ff' 
                            : '1.5px dashed rgba(24, 144, 255, 0.3)',
                          background: activeFieldName === 'studentName'
                            ? 'rgba(24, 144, 255, 0.15)'
                            : 'rgba(255, 255, 255, 0.3)',
                          boxShadow: activeFieldName === 'studentName'
                            ? '0 0 8px rgba(24, 144, 255, 0.3)'
                            : 'none',
                          transition: 'border-color 0.2s, background-color 0.2s',
                        }}
                      >
                        Student Name
                      </div>
                    )}
                    {templateLayout.courseName.visible && (
                      <div
                        onMouseDown={(e) => handleMouseDown(e, 'courseName')}
                        style={{
                          position: 'absolute',
                          top: `${templateLayout.courseName.top}%`,
                          left: `${templateLayout.courseName.left}%`,
                          transform: getFieldTransform(templateLayout.courseName.textAlign),
                          fontSize: `${templateLayout.courseName.fontSize}px`,
                          fontWeight: templateLayout.courseName.fontWeight,
                          color: templateLayout.courseName.color,
                          textAlign: templateLayout.courseName.textAlign,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                          cursor: 'move',
                          width: 'auto',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: activeFieldName === 'courseName' 
                            ? '1.5px solid #1890ff' 
                            : '1.5px dashed rgba(24, 144, 255, 0.3)',
                          background: activeFieldName === 'courseName'
                            ? 'rgba(24, 144, 255, 0.15)'
                            : 'rgba(255, 255, 255, 0.3)',
                          boxShadow: activeFieldName === 'courseName'
                            ? '0 0 8px rgba(24, 144, 255, 0.3)'
                            : 'none',
                          transition: 'border-color 0.2s, background-color 0.2s',
                        }}
                      >
                        Course Name
                      </div>
                    )}
                    {templateLayout.certificateNumber.visible && (
                      <div
                        onMouseDown={(e) => handleMouseDown(e, 'certificateNumber')}
                        style={{
                          position: 'absolute',
                          top: `${templateLayout.certificateNumber.top}%`,
                          left: `${templateLayout.certificateNumber.left}%`,
                          transform: getFieldTransform(templateLayout.certificateNumber.textAlign),
                          fontSize: `${templateLayout.certificateNumber.fontSize}px`,
                          fontWeight: templateLayout.certificateNumber.fontWeight,
                          color: templateLayout.certificateNumber.color,
                          textAlign: templateLayout.certificateNumber.textAlign,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                          cursor: 'move',
                          width: 'auto',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: activeFieldName === 'certificateNumber' 
                            ? '1.5px solid #1890ff' 
                            : '1.5px dashed rgba(24, 144, 255, 0.3)',
                          background: activeFieldName === 'certificateNumber'
                            ? 'rgba(24, 144, 255, 0.15)'
                            : 'rgba(255, 255, 255, 0.3)',
                          boxShadow: activeFieldName === 'certificateNumber'
                            ? '0 0 8px rgba(24, 144, 255, 0.3)'
                            : 'none',
                          transition: 'border-color 0.2s, background-color 0.2s',
                        }}
                      >
                        Cert No: DA-10293
                      </div>
                    )}
                    {templateLayout.issueDate.visible && (
                      <div
                        onMouseDown={(e) => handleMouseDown(e, 'issueDate')}
                        style={{
                          position: 'absolute',
                          top: `${templateLayout.issueDate.top}%`,
                          left: `${templateLayout.issueDate.left}%`,
                          transform: getFieldTransform(templateLayout.issueDate.textAlign),
                          fontSize: `${templateLayout.issueDate.fontSize}px`,
                          fontWeight: templateLayout.issueDate.fontWeight,
                          color: templateLayout.issueDate.color,
                          textAlign: templateLayout.issueDate.textAlign,
                          whiteSpace: 'nowrap',
                          pointerEvents: 'auto',
                          cursor: 'move',
                          width: 'auto',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: activeFieldName === 'issueDate' 
                            ? '1.5px solid #1890ff' 
                            : '1.5px dashed rgba(24, 144, 255, 0.3)',
                          background: activeFieldName === 'issueDate'
                            ? 'rgba(24, 144, 255, 0.15)'
                            : 'rgba(255, 255, 255, 0.3)',
                          boxShadow: activeFieldName === 'issueDate'
                            ? '0 0 8px rgba(24, 144, 255, 0.3)'
                            : 'none',
                          transition: 'border-color 0.2s, background-color 0.2s',
                        }}
                      >
                        Date: 2026-05-25
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#aaa', fontSize: '15px', textAlign: 'center', padding: 24 }}>
                    <UploadOutlined style={{ fontSize: '32px', marginBottom: '12px' }} />
                    <p>Please upload a high-quality certificate background design first</p>
                  </div>
                )}
              </div>
            </Col>

            <Col xs={24} lg={10}>
              <Form
                form={templateForm}
                layout="vertical"
                onFinish={handleCreateTemplate}
                style={{ paddingRight: 10 }}
              >
                <h3>Template Details</h3>
                <Form.Item
                  label="Template Name"
                  name="templateName"
                  rules={[{ required: true, message: 'Template name is required!' }]}
                >
                  <Input placeholder="e.g. Digital Academy Premium Course Template" />
                </Form.Item>

                <Form.Item
                  label="Associated Course"
                  name="courseId"
                  rules={[{ required: true, message: 'Please select a course!' }]}
                >
                  <Select placeholder="Select course">
                    {allCourses.map((course) => (
                      <Select.Option key={course.id} value={course.id}>
                        {course.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Background Template Upload (Landscape Image)"
                  name="backgroundImage"
                  rules={[{ required: !editingTemplateId, message: 'Template background image is required!' }]}
                >
                  <Upload
                    listType="picture"
                    fileList={templateFileList}
                    onChange={({ fileList: newFileList }) => setTemplateFileList(newFileList)}
                    beforeUpload={handleBeforeUploadTemplate}
                    maxCount={1}
                  >
                    {templateFileList.length < 1 && (
                      <Button icon={<UploadOutlined />} block>
                        Upload Background Image
                      </Button>
                    )}
                  </Upload>
                </Form.Item>

                <Divider style={{ margin: '15px 0' }} />

                <h3>Placement Adjustments</h3>
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', border: '1px solid #eee', marginBottom: 20 }}>
                  <Form.Item label="Select Field to Style">
                    <Radio.Group
                      value={activeFieldName}
                      onChange={(e) => setActiveFieldName(e.target.value)}
                      buttonStyle="solid"
                      style={{ width: '100%', display: 'flex', flexWrap: 'wrap' }}
                    >
                      <Radio.Button value="studentName" style={{ flex: 1, textAlign: 'center' }}>Student</Radio.Button>
                      <Radio.Button value="courseName" style={{ flex: 1, textAlign: 'center' }}>Course</Radio.Button>
                      <Radio.Button value="certificateNumber" style={{ flex: 1, textAlign: 'center' }}>Cert No</Radio.Button>
                      <Radio.Button value="issueDate" style={{ flex: 1, textAlign: 'center' }}>Date</Radio.Button>
                    </Radio.Group>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Visible">
                        <Switch
                          checked={templateLayout[activeFieldName].visible}
                          onChange={(val) => handleLayoutFieldChange(activeFieldName, 'visible', val)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Weight">
                        <Select
                          value={templateLayout[activeFieldName].fontWeight}
                          onChange={(val) => handleLayoutFieldChange(activeFieldName, 'fontWeight', val)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="normal">Normal</Select.Option>
                          <Select.Option value="bold">Bold</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item label={`Top Y Coordinate (${templateLayout[activeFieldName].top}%)`}>
                    <Slider
                      min={0}
                      max={100}
                      value={templateLayout[activeFieldName].top}
                      onChange={(val) => handleLayoutFieldChange(activeFieldName, 'top', val)}
                    />
                  </Form.Item>

                  <Form.Item label={`Left X Coordinate (${templateLayout[activeFieldName].left}%)`}>
                    <Slider
                      min={0}
                      max={100}
                      value={templateLayout[activeFieldName].left}
                      onChange={(val) => handleLayoutFieldChange(activeFieldName, 'left', val)}
                    />
                  </Form.Item>

                  <Form.Item label={`Font Size (${templateLayout[activeFieldName].fontSize}px)`}>
                    <Slider
                      min={8}
                      max={72}
                      value={templateLayout[activeFieldName].fontSize}
                      onChange={(val) => handleLayoutFieldChange(activeFieldName, 'fontSize', val)}
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Text Align">
                        <Select
                          value={templateLayout[activeFieldName].textAlign}
                          onChange={(val) => handleLayoutFieldChange(activeFieldName, 'textAlign', val)}
                          style={{ width: '100%' }}
                        >
                          <Select.Option value="left">Left</Select.Option>
                          <Select.Option value="center">Center</Select.Option>
                          <Select.Option value="right">Right</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Color">
                        <Input
                          type="color"
                          value={templateLayout[activeFieldName].color}
                          onChange={(e) => handleLayoutFieldChange(activeFieldName, 'color', e.target.value)}
                          style={{ padding: 0, border: 'none', height: '32px', width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={isLoading} block size="large">
                    {editingTemplateId ? 'Update Certificate Template' : 'Save Certificate Template'}
                  </Button>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Modal>
      )}

      {/* ISSUE CERTIFICATE MODAL */}
      {isAdminOrSuperAdmin && (
        <Modal
          title="Issue New Certificate"
          open={issueCertificateModalOpen}
          onCancel={() => {
            setIssueCertificateModalOpen(false);
            form.resetFields();
            setCertificateFileList([]);
          }}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleIssueCertificate}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.courseId) {
                handleCourseChange(changedValues.courseId);
              }
              if (changedValues.studentId || changedValues.courseId) {
                const studentId = allValues.studentId;
                const courseId = allValues.courseId;
                if (studentId && courseId) {
                  const student = allUsers.find((u) => u.id === studentId);
                  const course = allCourses.find((c) => c.id === courseId);
                  if (student && student.indexNumber && course) {
                    const displayCourseId = course.courseid || course.id;
                    form.setFieldsValue({
                      certificateNumber: `${displayCourseId}/${student.indexNumber}`,
                    });
                  }
                }
              }
            }}
          >
            <Form.Item label="Issuing Method" name="issuingMethod" initialValue="template">
              <Radio.Group
                onChange={(e) => setIssuingMethod(e.target.value)}
                buttonStyle="solid"
                style={{ width: '100%' }}
              >
                <Radio.Group value="template" buttonStyle="solid" style={{ width: '100%', display: 'flex' }}>
                  <Radio.Button value="template" style={{ flex: 1, textAlign: 'center' }}>
                    Use Certificate Template
                  </Radio.Button>
                  <Radio.Button value="manual" style={{ flex: 1, textAlign: 'center' }}>
                    Manual File Upload
                  </Radio.Button>
                </Radio.Group>
              </Radio.Group>
            </Form.Item>

            {issuingMethod === 'template' && (
              <Form.Item
                label="Certificate Template"
                name="templateId"
                rules={[{ required: true, message: 'Please select a certificate template!' }]}
              >
                <Select
                  placeholder="Select template design"
                  onChange={(val) => {
                    const selectedTpl = allTemplates.find((t) => t.TemplateID === val);
                    if (selectedTpl) {
                      form.setFieldsValue({ courseId: selectedTpl.CourseID });
                      handleCourseChange(selectedTpl.CourseID);
                      const studentId = form.getFieldValue('studentId');
                      if (studentId) {
                        const student = allUsers.find((u) => u.id === studentId);
                        const course = allCourses.find((c) => c.id === selectedTpl.CourseID);
                        if (student && student.indexNumber && course) {
                          const displayCourseId = course.courseid || course.id;
                          form.setFieldsValue({
                            certificateNumber: `${displayCourseId}/${student.indexNumber}`,
                          });
                        }
                      }
                    }
                  }}
                >
                  {allTemplates.map((t) => (
                    <Select.Option key={t.TemplateID} value={t.TemplateID}>
                      {t.TemplateName} ({t.courseName})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label="Student"
              name="studentId"
              rules={[{ required: true, message: 'Please select a student!' }]}
            >
              <Select
                showSearch
                placeholder="Select student (enrolled in course)"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {enrolledStudents.map((user) => (
                  <Select.Option
                    key={user.id}
                    value={user.id}
                    label={`${user.indexNumber} - ${user.name} (${user.email})`}
                  >
                    {user.indexNumber} - {user.name} ({user.email})
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Course"
              name="courseId"
              rules={[{ required: true, message: 'Please select a course!' }]}
            >
              <Select
                showSearch
                placeholder="Select course"
                optionFilterProp="label"
                disabled={issuingMethod === 'template'}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allCourses.map((course) => (
                  <Select.Option key={course.id} value={course.id} label={course.name}>
                    {course.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Certificate Number"
              name="certificateNumber"
              rules={[{ required: true, message: 'Please enter certificate number!' }]}
            >
              <Input placeholder="Enter certificate number (e.g. DA-CERT-2026-902)" />
            </Form.Item>

            {issuingMethod === 'manual' && (
              <Form.Item
                label="Certificate File Upload (PDF or Image)"
                name="certificateFile"
                rules={[{ required: true, message: 'Please upload a certificate file!' }]}
              >
                <Upload
                  listType="picture"
                  fileList={certificateFileList}
                  onChange={({ fileList: newFileList }) => setCertificateFileList(newFileList)}
                  beforeUpload={(file) => {
                    const isAllowed = file.type === 'application/pdf' || file.type.startsWith('image/');
                    if (!isAllowed) {
                      message.error(`${file.name} is not an image or PDF file`);
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                  maxCount={1}
                >
                  {certificateFileList.length < 1 && (
                    <Button icon={<UploadOutlined />}>Upload PDF / Image</Button>
                  )}
                </Upload>
              </Form.Item>
            )}

            <Form.Item label="Issue Date" name="issueDate" initialValue={dayjs()}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block size="large">
                Issue Certificate
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* VIEW & PRINT LANDSCAPE CERTIFICATE OVERLAY MODAL */}
      <Modal
        title={null}
        open={viewCertificateModalOpen}
        onCancel={() => setViewCertificateModalOpen(false)}
        footer={(
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }} className="no-print">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintCertificate}
            >
              Print Certificate
            </Button>
            <Button onClick={() => setViewCertificateModalOpen(false)}>Close</Button>
          </div>
        )}
        width={950}
        centered
        className="certificate-print-modal"
        wrapClassName="certificate-print-modal-wrap"
      >
        {viewCertificateModalOpen && selectedCertificate && (
          <style>
            {`
              @media print {
                @page {
                  size: ${printAspectRatio > 1 ? 'landscape' : 'portrait'};
                  margin: 0;
                }
              }
            `}
          </style>
        )}
        {selectedCertificate && (
          <div className="printable-certificate-outer">
            <div
              className="printable-certificate-canvas"
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: printAspectRatio,
                overflow: 'hidden',
                background: '#ffffff',
                border: '1px solid #d9d9d9',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              <img
                src={selectedCertificate.BackgroundImageURL}
                alt="Bg Design"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onLoad={(e) => {
                  const { naturalWidth, naturalHeight } = e.target;
                  if (naturalWidth && naturalHeight) {
                    setPrintAspectRatio(naturalWidth / naturalHeight);
                  }
                }}
              />
              {currentLayoutForPreview.studentName?.visible && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${currentLayoutForPreview.studentName.top}%`,
                    left: `${currentLayoutForPreview.studentName.left}%`,
                    transform: getFieldTransform(currentLayoutForPreview.studentName.textAlign),
                    fontSize: `${currentLayoutForPreview.studentName.fontSize}px`,
                    fontWeight: currentLayoutForPreview.studentName.fontWeight,
                    color: currentLayoutForPreview.studentName.color,
                    textAlign: currentLayoutForPreview.studentName.textAlign,
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    pointerEvents: 'none',
                  }}
                >
                  {selectedCertificate.studentName}
                </div>
              )}
              {currentLayoutForPreview.courseName?.visible && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${currentLayoutForPreview.courseName.top}%`,
                    left: `${currentLayoutForPreview.courseName.left}%`,
                    transform: getFieldTransform(currentLayoutForPreview.courseName.textAlign),
                    fontSize: `${currentLayoutForPreview.courseName.fontSize}px`,
                    fontWeight: currentLayoutForPreview.courseName.fontWeight,
                    color: currentLayoutForPreview.courseName.color,
                    textAlign: currentLayoutForPreview.courseName.textAlign,
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    pointerEvents: 'none',
                  }}
                >
                  {selectedCertificate.courseName}
                </div>
              )}
              {currentLayoutForPreview.certificateNumber?.visible && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${currentLayoutForPreview.certificateNumber.top}%`,
                    left: `${currentLayoutForPreview.certificateNumber.left}%`,
                    transform: getFieldTransform(currentLayoutForPreview.certificateNumber.textAlign),
                    fontSize: `${currentLayoutForPreview.certificateNumber.fontSize}px`,
                    fontWeight: currentLayoutForPreview.certificateNumber.fontWeight,
                    color: currentLayoutForPreview.certificateNumber.color,
                    textAlign: currentLayoutForPreview.certificateNumber.textAlign,
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    pointerEvents: 'none',
                  }}
                >
                  {selectedCertificate.CertificateNumber}
                </div>
              )}
              {currentLayoutForPreview.issueDate?.visible && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${currentLayoutForPreview.issueDate.top}%`,
                    left: `${currentLayoutForPreview.issueDate.left}%`,
                    transform: getFieldTransform(currentLayoutForPreview.issueDate.textAlign),
                    fontSize: `${currentLayoutForPreview.issueDate.fontSize}px`,
                    fontWeight: currentLayoutForPreview.issueDate.fontWeight,
                    color: currentLayoutForPreview.issueDate.color,
                    textAlign: currentLayoutForPreview.issueDate.textAlign,
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    pointerEvents: 'none',
                  }}
                >
                  {selectedCertificate.IssueDate
                    ? dayjs(selectedCertificate.IssueDate).format('YYYY-MM-DD')
                    : ''}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Certificates;
